import { Plus, RefreshCw, Wand2 } from 'lucide-react'

import { Button, CheckboxField } from '@krp/ui-kit'

import { InputField } from '../../../shared/ui/InputField'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import type { LinesActions, LinesVm } from '../model/viewModels'
import { DraftNote, SupplyLinesTable } from './SupplyLinesTable'

/** Раздел «Строки»: поиск, сопоставление с номенклатурой этой базы, коэффициент цены, новая строка и таблица. */
export function LinesSection({ vm, actions }: { vm: LinesVm; actions: LinesActions }) {
  return (
    <div className="onec-screen">
      <div className="onec-row">
        <InputField
          label="Поиск по строкам"
          type="search"
          value={vm.filter.search}
          onChange={(search) => actions.filterChanged({ search })}
        />
        <CheckboxField
          label="Только проблемные"
          checked={vm.filter.onlyProblems}
          onChange={(onlyProblems) => actions.filterChanged({ onlyProblems })}
        />
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
          Перечитать
        </Button>
      </div>
      {vm.editable && (
        <div className="onec-actions">
          <Button variant="secondary" icon={<Wand2 size={16} aria-hidden="true" />} onClick={() => actions.match(false)}>
            Сопоставить
          </Button>
          <Button variant="ghost" onClick={() => actions.match(true)}>
            Сопоставить заново (с ручными)
          </Button>
        </div>
      )}
      <OneCQueryState query={vm.query} emptyText="Строк нет" onRetry={actions.reload} />
      {vm.rows
        .filter((row) => row.draft !== null && (row.draft.state === 'conflict' || row.draft.state === 'failed' || row.draft.state === 'unknown'))
        .map((row) => (
          <div key={row.id} className="onec-section" aria-label={`Строка ${row.lineNumber}: не сохранено`}>
            <strong>
              Строка {row.lineNumber}: {row.name}
            </strong>
            <p className="onec-muted">
              Ваше: {[row.quantity, row.purchasePrice, row.amount].filter((cell) => cell.changed).map((cell) => cell.value).join(' / ') || '—'} · на
              сервере: {row.quantity.server} × {row.purchasePrice.server} = {row.amount.server}
            </p>
            <DraftNote row={row} actions={actions} />
          </div>
        ))}
      {vm.rows.length > 0 && <SupplyLinesTable rows={vm.rows} editable={vm.editable} actions={actions} />}

      {vm.editable && (
        <details className="onec-section">
          <summary>Коэффициент к закупочным ценам</summary>
          <div className="onec-row">
            <InputField label="Коэффициент (например 1,2)" inputMode="numeric" value={vm.priceFactor} onChange={actions.factorChanged} />
            <Button variant="secondary" onClick={actions.previewFactor} disabled={vm.priceFactor === '' || vm.factorProblem !== null}>
              Посмотреть
            </Button>
            <Button
              variant="primary"
              onClick={actions.applyFactor}
              disabled={vm.factorPreview === null || vm.factorPreview.applied || vm.factorProblem !== null}
            >
              Применить
            </Button>
          </div>
          {vm.factorProblem !== null && <p className="onec-problem">{vm.factorProblem}</p>}
          {vm.factorPreview !== null && (
            <p>
              {vm.factorPreview.applied ? 'Применено' : 'Предпросмотр'}: строк {vm.factorPreview.linesAffected}, пропущено{' '}
              {vm.factorPreview.linesSkipped}; сумма {vm.factorPreview.oldAmount} → {vm.factorPreview.newAmount}
            </p>
          )}
        </details>
      )}

      {vm.editable && (
        <details className="onec-section">
          <summary>
            <Plus size={16} aria-hidden="true" /> Новая строка
          </summary>
          <div className="onec-grid">
            <InputField label="Наименование" value={vm.addLine.name} onChange={(name) => actions.addLineChanged({ name })} />
            <InputField label="Артикул" value={vm.addLine.article} onChange={(article) => actions.addLineChanged({ article })} />
            <InputField label="Штрихкод" value={vm.addLine.barcode} onChange={(barcode) => actions.addLineChanged({ barcode })} />
            <InputField
              label="Количество"
              inputMode="numeric"
              value={vm.addLine.quantity}
              onChange={(quantity) => actions.addLineChanged({ quantity })}
            />
            <InputField
              label="Цена закупки"
              inputMode="numeric"
              value={vm.addLine.purchasePrice}
              onChange={(purchasePrice) => actions.addLineChanged({ purchasePrice })}
            />
          </div>
          {vm.addLineProblem !== null && vm.addLine.name !== '' && <p className="onec-problem">{vm.addLineProblem}</p>}
          <div className="onec-actions">
            <Button variant="primary" onClick={actions.addLine} disabled={vm.addLineProblem !== null}>
              Добавить строку
            </Button>
          </div>
        </details>
      )}
    </div>
  )
}
